import { Col, Div, Row } from "@/styles/layout";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getCurrentTheme } from "@/styles/theme";
import { Body } from "@/styles/typography";
import MyButton from "@/components/Button";
import { RxCross2 } from "react-icons/rx";
import TextEditor, { ContentEditableEvent } from "react-simple-wysiwyg";
import DOMPurify from "dompurify";
import { detectInvalidMergeTags } from "@/lib/mergeTags";

function getCharacterLength(htmlString: string) {
  const div = document.createElement("div");
  div.innerHTML = htmlString;

  return div.textContent!.length;
}

const EDITOR_WIDTH = 600;

export type MergeTags = {
  raw: string;
  description: string;
};

export interface EditorData {
  title: string;
  description?: string;
  field: string;
  value: string;
  mergeTags?: MergeTags[];
  maxLength?: number;
}
interface EditorProps {
  data: EditorData | undefined;
  onSave: (field: string, value: string) => Promise<void>;
  onClose: () => void;
}
const Editor = ({ data, onSave, onClose }: EditorProps) => {
  const [updatedValue, setUpdatedValue] = useState<string | undefined>(
    data?.value
  );

  const [characters, setCharacters] = useState(
    getCharacterLength(data?.value ?? "")
  );
  const [error, setError] = useState<string[]>([]);

  useEffect(() => {
    if (updatedValue && data) {
      const errors = [];

      // Length validation
      const length = getCharacterLength(updatedValue);
      setCharacters(length);
      if (data.maxLength) {
        if (length > data.maxLength) {
          errors.push(`Maximum character limit (${data.maxLength}) exceeded.`);
        }
      }

      // Tags validation
      const invalidTags = detectInvalidMergeTags(updatedValue, data.mergeTags);
      if (invalidTags.length) {
        errors.push(`Invalid merge tags detected: ${invalidTags.join(", ")}`);
      }

      setError(errors);
    }
  }, [data, updatedValue]);

  const handleSave = async () => {
    if (updatedValue && data) {
      await onSave(data?.field, updatedValue);
      onClose();
    }
  };

  useEffect(() => {
    setUpdatedValue(data?.value);
  }, [data]);

  return (
    <Div zIndex={9999998}>
      <AnimatePresence>
        {!!data && (
          <motion.div
            style={{
              position: "absolute",
              right: 0,
              left: 0,
              top: 0,
              bottom: 0,
              backgroundColor: getCurrentTheme().colors.overlay[6],
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!!data && (
          <motion.div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: EDITOR_WIDTH,
            }}
            transition={{ type: "spring", damping: 18 }}
            initial={{ x: EDITOR_WIDTH }}
            animate={{ x: 0 }}
            exit={{ x: EDITOR_WIDTH }}
          >
            <Col
              bg="primary.bgBase"
              alignItems="stretch"
              boxShadow={"l"}
              width="100%"
              height="100%"
            >
              <Col mb="16px" bg="primary.bgBase" px="24px">
                <Row
                  borderBottomStyle="solid"
                  borderColor="primary.borderSubtle"
                  borderBottomWidth="0.6px"
                  py="16px"
                  justifyContent={"center"}
                  position="relative"
                >
                  <Body
                    fontSize="18px"
                    fontWeight="600"
                    color="primary.textHigh"
                    textAlign={"center"}
                  >
                    {data.title}
                  </Body>

                  <Div
                    position="absolute"
                    left="0"
                    top="18px"
                    onClick={onClose}
                    style={{ cursor: "pointer" }}
                  >
                    <RxCross2 size={20} />
                  </Div>
                </Row>
              </Col>

              {(data.mergeTags?.length ?? 0) > 0 && (
                <Col px="24px">
                  <Col
                    borderColor="primary.borderSubtle"
                    borderStyle="none"
                    borderWidth="0.5px"
                    borderRadius="4px"
                    mb="24px"
                    bg="primary.bgBase"
                    p="16px"
                    boxShadow={"m"}
                  >
                    <Body fontSize="14px" fontWeight="400" mb="8px">
                      Personalisation tags:
                    </Body>

                    <Row justifyContent={"flex-start"}>
                      <Body fontSize="12px" fontWeight="600">
                        {data.mergeTags?.map((tag) => tag.raw).join(" - ")}
                      </Body>
                    </Row>
                  </Col>
                </Col>
              )}

              <Col mb="44px" flexGrow="1" minHeight="0" px="24px">
                <TextEditor
                  value={updatedValue}
                  containerProps={{
                    style: {
                      resize: "vertical",
                      height: "90%",
                      overflow: "auto",
                      backgroundColor: getCurrentTheme().colors.primary.bgBase,
                    },
                  }}
                  onChange={(e: ContentEditableEvent) => {
                    setUpdatedValue(DOMPurify.sanitize(e.target.value));
                  }}
                />

                <Row justifyContent={"flex-start"} mt="16px">
                  {error?.length > 0 && (
                    <Col>
                      {error.map((e) => (
                        <Body
                          key={e}
                          fontSize="14px"
                          color="danger.textLow"
                          fontWeight={400}
                        >
                          {e}
                        </Body>
                      ))}
                    </Col>
                  )}
                  <Body
                    fontSize="12px"
                    color="primary.textHigh"
                    fontWeight={400}
                    textAlign={"right"}
                    style={{ flexGrow: 1 }}
                  >
                    {`Characters: ${characters} ${
                      data.maxLength ? `/${data.maxLength}` : ""
                    }`}
                  </Body>
                </Row>
              </Col>

              <Col pb="24px" px="24px">
                <MyButton
                  variant="primary"
                  label="Save"
                  disabled={data?.value === updatedValue || error.length > 0}
                  onClick={handleSave}
                />
              </Col>
            </Col>
          </motion.div>
        )}
      </AnimatePresence>
    </Div>
  );
};

export default Editor;
